import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { TextField } from "@mui/material";
import pesquisaData from "../pesquisa.json";
import { fetchSection } from "../services/api";
import "./Pesquisa.css";

interface PesquisaItem {
  id: number;
  titulo: { pt: string; en: string; es: string };
  url: string;
  data: string;
  autores: string[];
  tipo: "tese" | "artigo";
  status: "concluido" | "em_andamento";
}

type SortOption = "data" | "titulo" | "autor";

const Pesquisa: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentView, setCurrentView] = useState<"menu" | "list">("menu");
  const [sortOption, setSortOption] = useState<SortOption>("data");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [data, setData] = useState<PesquisaItem[]>(
    pesquisaData as PesquisaItem[]
  );

  useEffect(() => {
    // Pull live content from the worker; fall back to the bundled copy.
    let cancelled = false;
    fetchSection<PesquisaItem[]>("pesquisa", pesquisaData as PesquisaItem[]).then(
      (d) => {
        if (!cancelled) setData(d);
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  // Get current language code
  const getCurrentLang = (): "pt" | "en" | "es" => {
    const lang = i18n.language;
    if (lang === "en-US") return "en";
    if (lang === "es-ES") return "es";
    return "pt";
  };

  const currentLang = getCurrentLang();

  // Filter data based on search
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const title = item.titulo[currentLang].toLowerCase();
      const authors = item.autores.join(" ").toLowerCase();
      const type = item.tipo;
      const searchLower = searchTerm.toLowerCase();

      return (
        title.includes(searchLower) ||
        authors.includes(searchLower) ||
        type.includes(searchLower)
      );
    });
  }, [data, searchTerm, currentLang]);

  // Sort function
  const sortItems = (items: PesquisaItem[]): PesquisaItem[] => {
    const sorted = [...items];
    sorted.sort((a, b) => {
      let result = 0;
      switch (sortOption) {
        case "data":
          // Convert DD/MM/YYYY to timestamp for comparison
          const dateA = new Date(
            a.data.split("/").reverse().join("-")
          ).getTime();
          const dateB = new Date(
            b.data.split("/").reverse().join("-")
          ).getTime();
          result = dateA - dateB;
          break;
        case "titulo":
          result = a.titulo[currentLang].localeCompare(b.titulo[currentLang]);
          break;
        case "autor":
          result = (a.autores[0] || "").localeCompare(b.autores[0] || "");
          break;
        default:
          return 0;
      }
      return sortDirection === "desc" ? -result : result;
    });
    return sorted;
  };

  // Separate items by status
  const concluidoItems = useMemo(() => {
    const items = filteredData.filter((item) => item.status === "concluido");
    return sortItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, sortOption, sortDirection, currentLang]);

  const emAndamentoItems = useMemo(() => {
    const items = filteredData.filter((item) => item.status === "em_andamento");
    return sortItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, sortOption, sortDirection, currentLang]);

  // Render research card
  const renderResearchCard = (item: PesquisaItem) => (
    <div
      key={item.id}
      className={`pesquisa-card pesquisa-card-${item.tipo}`}
      onClick={() => window.open(item.url, "_blank", "noopener,noreferrer")}
    >
      <span className="pesquisa-date">{item.data}</span>
      <span className={`pesquisa-type-badge pesquisa-type-${item.tipo}`}>
        {item.tipo === "tese" ? t("pesquisa.thesis") : t("pesquisa.article")}
      </span>
      <h4 className="pesquisa-title">{item.titulo[currentLang]}</h4>
      <div className="pesquisa-authors">
        {item.autores.map((autor, idx) => (
          <span key={idx} className="pesquisa-author">
            {autor}
            {idx < item.autores.length - 1 && ", "}
          </span>
        ))}
      </div>
    </div>
  );

  // Render menu view with two columns
  const renderMenu = () => (
    <>
      {/* Header */}
      <div className="pesquisa-header">
        <h1 className="pesquisa-main-title">{t("pesquisa.title")}</h1>
        <div className="pesquisa-header-controls">
          <TextField
            type="text"
            className="pesquisa-search-input"
            placeholder={t("pesquisa.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
          />
          <div className="pesquisa-sort-buttons">
            <button
              className={`pesquisa-sort-btn ${
                sortOption === "data" ? "active" : ""
              }`}
              onClick={() => {
                if (sortOption === "data") {
                  setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                } else {
                  setSortOption("data");
                  setSortDirection("desc");
                }
              }}
              title={t("pesquisa.sortDate")}
            >
              <span className="pesquisa-sort-icon">
                {sortOption === "data"
                  ? sortDirection === "desc"
                    ? "↓"
                    : "↑"
                  : "↕"}
              </span>
              <span className="pesquisa-sort-label">
                {t("pesquisa.sortDate")}
              </span>
            </button>
            <button
              className={`pesquisa-sort-btn ${
                sortOption === "titulo" ? "active" : ""
              }`}
              onClick={() => {
                if (sortOption === "titulo") {
                  setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                } else {
                  setSortOption("titulo");
                  setSortDirection("desc");
                }
              }}
              title={t("pesquisa.sortTitle")}
            >
              <span className="pesquisa-sort-icon">
                {sortOption === "titulo"
                  ? sortDirection === "desc"
                    ? "↓"
                    : "↑"
                  : "↕"}
              </span>
              <span className="pesquisa-sort-label">
                {t("pesquisa.sortTitle")}
              </span>
            </button>
            <button
              className={`pesquisa-sort-btn ${
                sortOption === "autor" ? "active" : ""
              }`}
              onClick={() => {
                if (sortOption === "autor") {
                  setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                } else {
                  setSortOption("autor");
                  setSortDirection("desc");
                }
              }}
              title={t("pesquisa.sortAuthor")}
            >
              <span className="pesquisa-sort-icon">
                {sortOption === "autor"
                  ? sortDirection === "desc"
                    ? "↓"
                    : "↑"
                  : "↕"}
              </span>
              <span className="pesquisa-sort-label">
                {t("pesquisa.sortAuthor")}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="pesquisa-menu">
        <div className="pesquisa-column">
          <div className="pesquisa-column-header">
            <h2 className="pesquisa-column-title">{t("pesquisa.completed")}</h2>
          </div>
          <div className="pesquisa-column-content">
            {concluidoItems.length === 0 ? (
              <div className="pesquisa-empty">
                <p>{t("pesquisa.noResults")}</p>
              </div>
            ) : (
              concluidoItems.map(renderResearchCard)
            )}
          </div>
        </div>
        <div className="pesquisa-column">
          <div className="pesquisa-column-header">
            <h2 className="pesquisa-column-title">
              {t("pesquisa.inProgress")}
            </h2>
          </div>
          <div className="pesquisa-column-content">
            {emAndamentoItems.length === 0 ? (
              <div className="pesquisa-empty">
                <p>{t("pesquisa.noResults")}</p>
              </div>
            ) : (
              emAndamentoItems.map(renderResearchCard)
            )}
          </div>
        </div>
      </div>
    </>
  );

  // Render list view
  const renderListView = () => (
    <div className="pesquisa-list-wrapper">
      {/* Header with back button, search, and sort */}
      <div className="pesquisa-list-header">
        <button
          className="pesquisa-back-btn"
          onClick={() => setCurrentView("menu")}
        >
          ← {t("pesquisa.back")}
        </button>
        <TextField
          type="text"
          className="pesquisa-search-input"
          placeholder={t("pesquisa.searchPlaceholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
        />
        <div className="pesquisa-sort-buttons">
          <button
            className={`pesquisa-sort-btn ${
              sortOption === "data" ? "active" : ""
            }`}
            onClick={() => {
              if (sortOption === "data") {
                setSortDirection(sortDirection === "desc" ? "asc" : "desc");
              } else {
                setSortOption("data");
                setSortDirection("desc");
              }
            }}
            title={t("pesquisa.sortDate")}
          >
            <span className="pesquisa-sort-icon">
              {sortOption === "data"
                ? sortDirection === "desc"
                  ? "↓"
                  : "↑"
                : "↕"}
            </span>
            <span className="pesquisa-sort-label">
              {t("pesquisa.sortDate")}
            </span>
          </button>
          <button
            className={`pesquisa-sort-btn ${
              sortOption === "titulo" ? "active" : ""
            }`}
            onClick={() => {
              if (sortOption === "titulo") {
                setSortDirection(sortDirection === "desc" ? "asc" : "desc");
              } else {
                setSortOption("titulo");
                setSortDirection("desc");
              }
            }}
            title={t("pesquisa.sortTitle")}
          >
            <span className="pesquisa-sort-icon">
              {sortOption === "titulo"
                ? sortDirection === "desc"
                  ? "↓"
                  : "↑"
                : "↕"}
            </span>
            <span className="pesquisa-sort-label">
              {t("pesquisa.sortTitle")}
            </span>
          </button>
          <button
            className={`pesquisa-sort-btn ${
              sortOption === "autor" ? "active" : ""
            }`}
            onClick={() => {
              if (sortOption === "autor") {
                setSortDirection(sortDirection === "desc" ? "asc" : "desc");
              } else {
                setSortOption("autor");
                setSortDirection("desc");
              }
            }}
            title={t("pesquisa.sortAuthor")}
          >
            <span className="pesquisa-sort-icon">
              {sortOption === "autor"
                ? sortDirection === "desc"
                  ? "↓"
                  : "↑"
                : "↕"}
            </span>
            <span className="pesquisa-sort-label">
              {t("pesquisa.sortAuthor")}
            </span>
          </button>
        </div>
      </div>

      {/* Content with both sections */}
      <div className="pesquisa-list-content">
        {/* Completed Section */}
        {concluidoItems.length > 0 && (
          <div className="pesquisa-group">
            <h3 className="pesquisa-group-title">{t("pesquisa.completed")}</h3>
            {concluidoItems.map(renderResearchCard)}
          </div>
        )}

        {/* In Progress Section */}
        {emAndamentoItems.length > 0 && (
          <div className="pesquisa-group">
            <h3 className="pesquisa-group-title">{t("pesquisa.inProgress")}</h3>
            {emAndamentoItems.map(renderResearchCard)}
          </div>
        )}

        {/* Empty state */}
        {concluidoItems.length === 0 && emAndamentoItems.length === 0 && (
          <div className="pesquisa-empty">
            <p>{t("pesquisa.noResults")}</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <section id="research" className="pesquisa-section">
      <div className="pesquisa-container">
        {currentView === "menu" && renderMenu()}
        {currentView === "list" && renderListView()}
      </div>
    </section>
  );
};

export default Pesquisa;
